import { Component, Input, SimpleChanges, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';
import { EstimateRenderData } from '@encounter';
import { EstimateConfigModel } from '../../../models/estimateConfigModel';
import { FeatureService } from '@revspringinc/shared';
import { Alert } from '@pa-ui';
import { EstimateApiService } from '../../../services';

@Component({
    selector: 'estimate-default-renderer',
    templateUrl: './default-estimate-renderer.component.html',
    styleUrls: ['../../../scss/styles.scss'],
    standalone: false
})
export class DefaultEstimateRendererComponent implements OnDestroy {
    estimateConfig: EstimateConfigModel | undefined;
    disclaimerAlert: Alert | undefined;
    features: any;
    activeFeatures: any = {};
    private serviceFeatures: any = {};
    private storeAcknowledgedEstimateDisclaimerAt: string | null | undefined;
    public npiText: string = '';

    private destroy$ = new Subject<void>();

    @Input()
    public estimate: EstimateRenderData | undefined;

    @Input() public loading: boolean | undefined;
    @Input() public estimateDisclaimerAcknowledged: Date | string | undefined;
    @Input() public appName!: 'patientPortal' | 'pricing' | 'registrar';
    @Input() public externalFeatures: any;
    @Input() public encounterId: number | undefined;
    @Input() public acknowledgedEstimateDisclaimerAt: string | null | undefined;

    public showDetails: boolean = false;
    public canShowDetails: boolean = true;
    public showAcknowledgeRow: boolean = false;
    public acknowledgeName: string = '';
    public isSubmitting: boolean = false;
    public acknowledgedEstimateDisclaimerAtLocal: string | null | undefined;

    constructor(
        public readonly featureService: FeatureService,
        private readonly estimateApiService: EstimateApiService,
        private readonly store$: Store<any>,
    ) {
        this.featureService.features$
            .pipe(takeUntil(this.destroy$))
            .subscribe((features) => {
                this.serviceFeatures = features || {};
                this.setActiveFeatures();
            });

        this.store$
            .pipe(
                select((state) => {
                    return state?.consumer?.acknowledgedEstimateDisclaimerAt
                        ?? null;
                }),
                takeUntil(this.destroy$),
            )
            .subscribe((acknowledgedEstimateDisclaimerAt) => {
                this.storeAcknowledgedEstimateDisclaimerAt = acknowledgedEstimateDisclaimerAt;
                this.setAcknowledgeVisibility();
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['externalFeatures']) {
            this.features = this.externalFeatures;
            this.setActiveFeatures();
        }

        if (changes['appName']) {
            this.setAcknowledgeVisibility();
        }

        if (changes['acknowledgedEstimateDisclaimerAt']) {
            this.setAcknowledgeVisibility();
        }

        if (changes['estimate']) {
            this.estimateConfig = this.estimate?.estimateConfig?.estimate || this.estimate;

            this.disclaimerAlert = {
                icon: 'icon-alert',
                priority: 'warning',
                message: this.estimateConfig?.disclaimer || '',
                title: 'Disclaimer',
            };

            this.showDetails = this.estimateConfig?.settings?.details?.visible === 'expanded' || false;
            this.canShowDetails = this.estimateConfig?.settings?.details?.visible !== 'hidden';

            const templateText = this.estimateConfig?.estimateNpiValue || '';

            if (this.estimate?.providerNpi != null) {
                const variables = {
                    ...this.estimateConfig?.variables,
                    npi: this.estimate.providerNpi,
                };
                this.npiText = this.interpolateVariables(templateText, variables);
            } else {
                this.npiText = '';
            }
        }
    }

    private interpolateVariables(value: string, variables: Record<string, any>): string {
        if (!value || !variables) {
            return value || '';
        }
        let hasMissingVariable = false;
        const interpolatedValue = value.replace(/\{\{\s*variable\.(\w+)\s*\}\}/g, (_match, key) => {
            const variable = variables[key];
            if (variable === undefined || variable === null) {
                hasMissingVariable = true;
                return '';
            }
            return String(variable);
        });
        return hasMissingVariable ? '' : interpolatedValue;
    }

    public startsWith(value: string, key: string): boolean {
        return value ? value.startsWith(key) : false;
    }

    public toText(value: any) {
        return value.toString().replace(/\$/g, '');
    }

    public get canAcknowledge(): boolean {
        return !!this.acknowledgeName?.trim() && !this.isSubmitting;
    }

    toggleDetails() {
        this.showDetails = !this.showDetails;
    }

    public acknowledgeEstimate(): void {
        if (!this.canAcknowledge || !this.encounterId) {
            return;
        }

        const acknowledgeName = this.acknowledgeName.trim();

        this.isSubmitting = true;

        this.estimateApiService.acknowledgeEstimateDisclaimer({
            encounterId: this.encounterId,
            acknowledgeName,
        }).pipe(
            takeUntil(this.destroy$),
        ).subscribe({
            next: (response) => {
                this.isSubmitting = false;
                const acknowledgedEstimateDisclaimerAt =
                    response?.acknowledgedEstimateDisclaimerAt
                    ?? new Date().toISOString();

                this.acknowledgedEstimateDisclaimerAtLocal = acknowledgedEstimateDisclaimerAt;
                this.store$.dispatch({
                    type: '[Consumer] Set Encounter Data',
                    acknowledgedEstimateDisclaimerAt,
                });
                this.acknowledgeName = '';
                this.setAcknowledgeVisibility();
            },
            error: (err) => {
                this.isSubmitting = false;
                console.error('Failed to acknowledge estimate', err);
            },
        });
    }

    private setActiveFeatures(): void {
        if (this.features && Object.keys(this.features).length > 0) {
            this.activeFeatures = this.features;
            this.setAcknowledgeVisibility();
            return;
        }

        this.activeFeatures = this.serviceFeatures;
        this.setAcknowledgeVisibility();
    }

    private setAcknowledgeVisibility(): void {
        const acknowledgedEstimateDisclaimerAt =
            this.acknowledgedEstimateDisclaimerAtLocal
            ?? this.acknowledgedEstimateDisclaimerAt
            ?? this.storeAcknowledgedEstimateDisclaimerAt;
        const hasAcknowledgedDisclaimer = !!acknowledgedEstimateDisclaimerAt;
        this.showAcknowledgeRow = !!this.activeFeatures?.['acknowledgedEstimateDisclaimer']
            && this.appName === 'patientPortal'
            && !hasAcknowledgedDisclaimer;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
